import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from '../../../config/theme';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import moment from 'moment';
import { numberAsCurrency } from '../../utils/index';
import axios from 'axios';
import DescriptionIcon from '@material-ui/icons/Description';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Link } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  detailsShell: {
    marginTop: '8px',
    marginBottom: '20px',
  },
  detailsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '14px 16px',
    cursor: 'pointer',
    border: '1px solid #E2E7F0',
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    textAlign: 'left',
    width: '100%',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: '#F8FAFF',
    },
  },
  detailsHeaderOpen: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: 'none',
  },
  detailsHeaderLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    minWidth: 0,
  },
  docIcon: {
    color: '#3B7E3D',
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  detailsTitleBlock: {
    minWidth: 0,
  },
  detailsTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#1A1A1A',
    lineHeight: 1.3,
  },
  detailsLink: {
    display: 'block',
    marginTop: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#3B7E3D',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  expandIcon: {
    color: '#3B7E3D',
    transition: 'transform 0.2s',
    flexShrink: 0,
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  detailsBody: {
    border: '1px solid #E2E7F0',
    borderTop: 'none',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    backgroundColor: '#FFFFFF',
    padding: '16px',
    textAlign: 'left',
  },
  main: {
    color: '#1A1A1A',
    fontSize: '0.875rem',
    '& hr': {
      border: 'none',
      borderTop: '1px solid #EEF1F6',
      margin: '12px 0',
    },
  },
  sectionBlock: {
    marginBottom: '14px',
  },
  sectionTitle: {
    fontSize: '0.86rem',
    fontWeight: 800,
    color: '#2D6E2F',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    marginBottom: '8px',
  },
  instant: {
    backgroundColor: 'rgb(117, 236, 80)',
    color: 'rgb(18, 4, 120)',
    borderRadius: '3px',
    fontWeight: 600,
    padding: '1px 2px',
  },
  subcampaignBlock: {
    marginBottom: '12px',
    paddingBottom: '12px',
    borderBottom: '1px solid #EEF1F6',
    '&:last-child': {
      marginBottom: 0,
      paddingBottom: 0,
      borderBottom: 'none',
    },
  },
  subcampaignTitle: {
    fontWeight: 700,
    color: '#1A1A1A',
    fontSize: '0.9rem',
    marginBottom: '2px',
  },
  subcampaignMeta: {
    color: '#666666',
    fontSize: '0.8rem',
    marginBottom: '8px',
  },
  subcampaignNumbers: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  subcampaignChip: {
    color: '#243241',
    fontWeight: 500,
    fontSize: '0.7rem',
    backgroundColor: '#8EEA8E',
    borderRadius: '7px',
    padding: '4px 8px',
    lineHeight: 1.1,
  },
  subcampaignChipWinner: {
    backgroundColor: 'rgb(117, 236, 80)',
    color: 'rgb(18, 4, 120)',
    fontWeight: 600,
  },
}));

export default function OrderDetails({ order }) {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(false);
  const [numbers, setNumbers] = React.useState([]);
  const [expanded, setExpanded] = React.useState(() => order && order.status !== 'waiting_payment');

  function fetchMoreNumbers() {
    setLoading(true);
    axios
      .get(`/api/v1/purchase_orders/${order.id}/numbers?offset=${numbers.length + order.numbers.length}`)
      .then((response) => {
        setNumbers(numbers.concat(response.data));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  if (!order) return null;

  function padn(n) {
    return n.toString().padStart(order.campaign.digits, '0');
  }

  function formatted_number(number, index) {
    if ((order.instant_prizes || []).some((item) => item.number === number))
      return (
        <span title="Número premiado!" className={classes.instant}>
          {' '}
          {padn(number)}
        </span>
      );
    else return index != 0 ? `, ${padn(number)}` : padn(number);
  }

  const comprasPath = order.campaign?.influencer_username
    ? `/${order.campaign.influencer_username}/site/compras`
    : '/site/compras';

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.detailsShell}>
        <button
          type="button"
          className={`${classes.detailsHeader} ${expanded ? classes.detailsHeaderOpen : ''}`}
          onClick={() => setExpanded((e) => !e)}
        >
          <div className={classes.detailsHeaderLeft}>
            <DescriptionIcon className={classes.docIcon} />
            <div className={classes.detailsTitleBlock}>
              <div className={classes.detailsTitle}>Detalhes das compras e dos meus títulos</div>
              <Link to={comprasPath} className={classes.detailsLink} onClick={(e) => e.stopPropagation()}>
                Ver meus dados e títulos
              </Link>
            </div>
          </div>
          <ExpandMoreIcon className={`${classes.expandIcon} ${expanded ? classes.expandIconOpen : ''}`} />
        </button>
        <Collapse in={expanded}>
          <div className={classes.detailsBody}>
            <div className={classes.main}>
              <div className={classes.sectionBlock}>
                <div className={classes.sectionTitle}>Detalhes do comprador</div>
                <Typography variant="body1">
                  <b>Comprador: </b>
                  {order.customer.name}
                </Typography>
                <hr />
                <Typography variant="body1">
                  <b>CPF: </b>
                  {order.customer.cpf}
                </Typography>
                <hr />
                <Typography variant="body1">
                  <b>E-mail: </b>
                  {order.customer.email}
                </Typography>
                <hr />
                <Typography variant="body1">
                  <b>Telefone: </b>
                  {order.customer.phone}
                </Typography>
              </div>
              <div className={classes.sectionBlock}>
                <div className={classes.sectionTitle}>Detalhes da compra</div>
                <Typography variant="body1">
                  <b>Data/horário: </b>
                  {moment(order.created_at).format('DD/MM/YYYY [às] HH:mm')}
                </Typography>
                <hr />
                <Typography variant="body1">
                  <b>Situação: </b>
                  {order.user_status}
                </Typography>
                <hr />
                <Typography variant="body1">
                  <b>Total: </b>
                  {numberAsCurrency(order.total_value)}
                </Typography>
                <hr />
                <Typography variant="body1">
                  <b>Títulos comprados: </b>
                  {order.numbers_count}
                </Typography>
                <hr />
                {order.status != 'canceled' && (
                  <Typography variant="body1">
                    <b>Títulos: </b>
                    {order.numbers.map((number, index) => formatted_number(number, index))}
                    {numbers.map((number, index) => formatted_number(number, 1))}
                    {['paid'].includes(order.status) && 'Títulos sendo emitidos, aguarde...'}
                    {['paid'].includes(order.status) &&
                      moment(order.updated_at).isBefore(moment().subtract(3, 'minutes')) && (
                        <>
                          <br />
                          <b>
                            A emissão dos títulos pode estar demorando um pouco mais que o normal, mas não se
                            preocupe, suas chances de ganhar estão garantidas!
                          </b>
                        </>
                      )}
                    <br />
                    {['completed'].includes(order.status) &&
                      order.numbers_count > numbers.length + order.numbers.length && (
                        <button type="button" onClick={fetchMoreNumbers} disabled={loading}>
                          {loading ? 'Carregando...' : 'Carregar mais'}
                        </button>
                      )}
                  </Typography>
                )}
              </div>
              {order.status != 'canceled' && (order.subcampaign_tickets || []).length > 0 && (
                <div className={classes.sectionBlock}>
                  <div className={classes.sectionTitle}>Bilhetes extras</div>
                  {(order.subcampaign_tickets || []).map((sub) => (
                    <div key={sub.subcampaign_id} className={classes.subcampaignBlock}>
                      <div className={classes.subcampaignTitle}>
                        {sub.title}{sub.prize_description ? ` — ${sub.prize_description}` : ''}
                      </div>
                      {sub.draw_time && (
                        <div className={classes.subcampaignMeta}>
                          Sorteio {moment(sub.draw_time).format('DD/MM/YYYY [às] HH:mm')}
                        </div>
                      )}
                      <div className={classes.subcampaignNumbers}>
                        {(sub.numbers || []).map((number, index) => {
                          const isWinner = (order.instant_prizes || []).some(
                            (item) => item.number === number && item.campaign_id === sub.subcampaign_id
                          );
                          return (
                            <span
                              key={index}
                              className={`${classes.subcampaignChip}${isWinner ? ` ${classes.subcampaignChipWinner}` : ''}`}
                              title={isWinner ? 'Número premiado!' : undefined}
                            >
                              {number}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>
    </ThemeProvider>
  );
}
